import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { WorkflowBuilderStoreService } from '../../services/workflow-builder-store.service';
import { buildLayout, LayoutNode } from '../../core/workflow-layout';
import { scaledCanvasSize } from '../../core/canvas-sizing';
import {
  isAuthorised,
  validate,
  Warning,
} from '../../core/workflow-validation';
import { WORKFLOW_VALIDATION_CONFIG } from '../../core/workflow-validation.config';
import { fromJson, toJson } from '../../core/workflow-mapper';
import { DEFAULT_STATE_COLOUR, resolveStateColour } from '../../core/catalogs';
import {
  IAction,
  IBuilderModel,
  IEndState,
  IStateMeta,
  ITransition,
} from '../../models/workflow-builder.model';
import {
  InspectorActionDeleteRequest,
  InspectorTarget,
} from '../inspector/inspector.component';
import { ServiceChangeRequestService } from '../../../../../core/services/service-change-request.service';

export type AnchorName = 't1' | 't2' | 't3' | 'b1' | 'b2' | 'b3' | 'l' | 'r';
export interface FreeEdge {
  id: string;
  tid: string;
  srcCode: string;
  dstCode: string;
  srcName: AnchorName;
  dstName: AnchorName;
  sx: number;
  sy: number;
  ex: number;
  ey: number;
  d: string;
  mx: number;
  my: number;
  label: string;
  actor: string;
  colour: string;
  isSystem: boolean;
  unauthorized: boolean;
  /** For a two-outcome (conditional) transition: '①' = endStateOne, '②' = endStateTwo, '' = single outcome. */
  outcome: '' | '①' | '②';
  /** For guarded transitions routed through a ◆ decision: 'false' = ◆→①, 'true' = ◆→②, '' = normal/event edge. */
  branch: '' | 'true' | 'false';
}

/**
 * Container for the v2 workflow builder. The ONLY component that pulls in the
 * shared stylesheet (ViewEncapsulation.None) and wraps its template in `.iwb-v2`.
 * Presentational children render the model; the store owns state.
 */
@Component({
  selector: 'irembogov-workflow-builder-v2',
  templateUrl: './workflow-builder.component.html',
  styleUrls: ['../../styles/workflow-builder-v2.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [WorkflowBuilderStoreService],
})
export class WorkflowBuilderComponent
  implements OnChanges, OnDestroy, AfterViewChecked
{
  @Input() workflowJson: unknown;
  /** Service form fields — passed straight through to the reused notification / receipt managers. */
  @Input() formFields: unknown;
  /** Organisation id — passed straight through to the reused office-assignment manager. */
  @Input() organizationId?: string;
  @Output() workflowJsonChange = new EventEmitter<unknown>();

  model: IBuilderModel = { transitions: [], states: [], topRaw: {} };
  layout: LayoutNode[] = [];
  json: unknown = { workflow: [] };
  inspector: InspectorTarget | null = null;
  composerFrom: string | null = null;
  /** Whether the add-event composer is open. */
  composerOpen = false;
  /** True when composing the mandatory first event (NEW → APPLY) → lock the event to APPLY. */
  composerFirst = false;
  /** Where to render the add-event composer (near the click; clamped to viewport). */
  composerPos = { x: 0, y: 90 };
  /** Which reused wizard/v1 manager (if any) is open as a drawer. */
  manager: 'notification' | 'office' | 'receipt' | null = null;
  warnings: Warning[] = [];
  /** State briefly flashed after clicking its warning (so the user sees the source). */
  focusedStateCode: string | null = null;
  private focusClearTimer: ReturnType<typeof setTimeout> | null = null;
  showTech = false;

  // ── view controls (M7B: fullscreen + zoom/pan over the auto-layout) ──
  @ViewChild('canvasEl') canvasRef?: ElementRef<HTMLElement>;
  @ViewChild('flowEl') flowRef?: ElementRef<HTMLElement>;
  @ViewChild('flowWrap') flowWrapRef?: ElementRef<HTMLElement>;
  scale = 1;
  isFullscreen = false;
  showJsonEditor = false;
  // Monaco loads lazily (~seconds) the first time an editor mounts, which left the
  // state JSON tab / fullscreen editor blank on first open. Warm it up in the
  // background with a hidden editor so those open instantly; drop it once loaded.
  monacoWarm = false;
  monacoWarmupOptions = {
    theme: 'vs',
    language: 'json',
    automaticLayout: false,
    minimap: { enabled: false },
    readOnly: true,
    lineNumbers: 'off' as const,
  };
  onMonacoWarm(): void {
    this.monacoWarm = true;
    this.cdr.markForCheck();
  }

  // ── free mode (7B.3): absolutely-positioned, draggable state boxes (DEFAULT) ──
  freeMode = true;
  /** Tidy-up flow direction: top-down (vertical) or left-right (horizontal, fits wide graphs). */
  tidyDir: 'vertical' | 'horizontal' = 'vertical';
  freePos: Record<string, { x: number; y: number }> = {};
  freeSize = { w: 900, h: 600 };
  /** Render offset so the left/top-most box sits at MARGIN — lets boxes move left without a "wall". */
  private off = { x: 0, y: 0 };
  private readonly MARGIN = 48;
  private seeded = false;
  private pendingTidy = false;
  /** Code of the most recently added state — scrolled into view once it renders. */
  private justAddedCode: string | null = null;
  /** Guards a single extra measure pass so a just-painted node's edges snap to its real size. */
  private remeasureScheduled = false;
  /** Remember where/which-direction the last add-event was triggered, to place the new state. */
  private lastComposerFrom: string | null = null;
  private lastComposerDir = { dx: 0, dy: 1 };
  freeEdges: FreeEdge[] = [];
  /**
   * Render-space box (centre + size) of each guarded transition's ◆ decision diamond, keyed
   * by `cond:<tid>`. Lets the diamond participate in the generic anchor/re-route geometry as a
   * virtual node (it is NOT draggable / not persisted — position is derived from its states).
   */
  private condBox: Record<
    string,
    { cx: number; cy: number; w: number; h: number }
  > = {};
  /** UI-only anchor overrides per edge (re-routing); NOT written to workflow.json. */
  freeEdgeAnchors: Record<string, { src?: AnchorName; dst?: AnchorName }> = {};
  /** Named anchor positions (fraction of the box) — connection points around a state. */
  readonly anchorNames: AnchorName[] = [
    't1',
    't2',
    't3',
    'r',
    'b3',
    'b2',
    'b1',
    'l',
  ];
  private readonly anchorFr: Record<AnchorName, { fx: number; fy: number }> = {
    t1: { fx: 0.25, fy: 0 },
    t2: { fx: 0.5, fy: 0 },
    t3: { fx: 0.75, fy: 0 },
    b1: { fx: 0.25, fy: 1 },
    b2: { fx: 0.5, fy: 1 },
    b3: { fx: 0.75, fy: 1 },
    l: { fx: 0, fy: 0.5 },
    r: { fx: 1, fy: 0.5 },
  };
  private nodeSizes: Record<string, { w: number; h: number }> = {};
  private _edgesKey = '';
  private panning = false;
  private panStart = { x: 0, y: 0, sl: 0, st: 0 };
  private sizingDirty = true;

  private sub = new Subscription();
  private loaded = false;
  /**
   * The builder owns the workflow state after the first load. The host re-binds our emitted
   * value back into [workflowJson] on every edit (echo), autosave round-trip and json-editor
   * sync; reloading those would regenerate transition ids (closing the inspector) and re-seed
   * positions (re-running the auto-layout). So we load exactly once and ignore later inputs.
   */
  private inputLoaded = false;

  /** Full application-state catalog (code + label + backend colour) for the destination picker. */
  apiStates: IStateMeta[] = [];

  constructor(
    private store: WorkflowBuilderStoreService,
    private cdr: ChangeDetectorRef,
    private crs: ServiceChangeRequestService
  ) {
    this.sub.add(
      this.crs.getApplicationStates().subscribe({
        next: (res: any) => {
          const list: any[] = res?.data?.content ?? res?.data ?? [];
          this.apiStates = list
            // Backend contract: `stateName` is the machine CODE, `stateLabel` the human
            // name, `stateColor` a Bootstrap class. (Older field names kept as fallbacks.)
            .filter(s => s && (s.stateName ?? s.stateCode))
            .map(s => ({
              code: s.stateName ?? s.stateCode,
              name: s.stateLabel ?? s.stateName ?? s.stateCode,
              colour: resolveStateColour(
                s.stateColor ?? s.stateColour ?? s.colour ?? s.color
              ),
            }));
          this.cdr.markForCheck();
        },
        error: () => {
          /* non-fatal: composer falls back to the workflow's own states */
        },
      })
    );
    this.sub.add(
      this.store.model$.subscribe(m => {
        this.model = m;
        this.layout = buildLayout(m);
        this.json = toJson(m);
        this.warnings = validate(m);
        this.sizingDirty = true; // layout changed → re-measure the scaled scroll area
        // Seed only once the model actually has states. The store is a BehaviorSubject, so the
        // constructor subscription fires first with the EMPTY initial model — seeding then would
        // mark `seeded` (and pendingTidy) before the real workflow loads, so its saved positions
        // would never be honoured.
        if (this.freeMode && !this.seeded && m.states.length > 0) {
          this.seeded = true;
          this.seedFreePositions();
        } else if (this.freeMode && this.seeded) {
          this.placeMissingPositions();
          this.scheduleFreeRecompute();
        }
        // Emit user edits to the host save path; skip the initial load emit.
        if (this.loaded) {
          this.workflowJsonChange.emit(this.json);
        }
        this.loaded = true;
        this.cdr.markForCheck();
      })
    );
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (!ch['workflowJson']) return;
    // Load the workflow ONCE. The host continuously re-binds our own emitted value back into
    // [workflowJson] (edit echoes, autosave round-trips, json-editor sync). Reloading those would
    // regenerate transition ids — closing the open inspector — and re-seed free positions,
    // making the states visibly rearrange on their own. After the first real load the builder
    // is the source of truth; later parent updates are ignored. (A fresh workflow remounts this
    // component, so it re-loads correctly then.)
    if (this.inputLoaded) return;
    const workflowJson = this.workflowJsonForLoad(this.workflowJson);
    if (!workflowJson) return; // wait for the real workflow document
    this.inputLoaded = true;
    this.store.load(workflowJson);
  }

  private workflowJsonForLoad(raw: unknown): unknown | null {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
      return null;
    }
    const doc = raw as Record<string, unknown>;
    if (!Object.prototype.hasOwnProperty.call(doc, 'workflow')) {
      return null;
    }
    if (Array.isArray(doc['workflow'])) {
      return raw;
    }
    if (doc['workflow'] == null) {
      return { ...doc, workflow: [] };
    }
    return null;
  }

  ngAfterViewChecked(): void {
    // Keep the scrollable area sized to the SCALED content so zoom-in can pan.
    if (this.sizingDirty) {
      const flow = this.flowRef?.nativeElement;
      const wrap = this.flowWrapRef?.nativeElement;
      if (flow && wrap) {
        // In free mode the nodes are absolutely positioned, so `.flow` can
        // measure as 0px wide. Reserve the free canvas size instead so the
        // graph starts in view and zoom still has a real scroll area.
        const size = scaledCanvasSize(
          this.freeMode,
          this.freeSize,
          this.scale,
          { w: flow.offsetWidth, h: flow.offsetHeight }
        );
        wrap.style.width = size.w + 'px';
        wrap.style.height = size.h + 'px';
        this.sizingDirty = false;
      }
    }
  }
  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  // ── zoom / pan / fullscreen ──
  zoom(dir: 'in' | 'out' | 'reset'): void {
    if (dir === 'reset') {
      this.scale = 1;
    } else {
      const next = dir === 'in' ? this.scale * 1.15 : this.scale / 1.15;
      this.scale = Math.min(3, Math.max(0.25, +next.toFixed(3)));
    }
    this.sizingDirty = true;
  }
  /** Ctrl/Cmd + wheel = zoom-to-cursor (plain wheel keeps native scroll). */
  onWheel(e: WheelEvent): void {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    const el = this.canvasRef?.nativeElement;
    if (!el) return;
    const prev = this.scale;
    const next = Math.min(
      3,
      Math.max(0.25, +(prev * (e.deltaY < 0 ? 1.1 : 1 / 1.1)).toFixed(3))
    );
    if (next === prev) return;
    const rect = el.getBoundingClientRect();
    const px = e.clientX - rect.left + el.scrollLeft;
    const py = e.clientY - rect.top + el.scrollTop;
    const k = next / prev;
    this.scale = next;
    this.sizingDirty = true;
    // re-measure synchronously, then keep the cursor point stationary
    this.ngAfterViewChecked();
    el.scrollLeft = px * k - (e.clientX - rect.left);
    el.scrollTop = py * k - (e.clientY - rect.top);
  }
  /** Grab-to-pan on empty canvas (adjusts native scroll; ignores clicks on nodes/controls). */
  startPan(e: PointerEvent): void {
    if (e.button !== 0) return; // only left-button pans (right-click → context menu)
    const t = e.target as HTMLElement;
    // NOTE: include `.evlabel`/`.edge-labels`/`.endpoint` — without them a pointerdown on an event
    // label starts a pan and captures the pointer, so the label's (click) never fires (the click is
    // redirected to the canvas) and you can't open the event config. (Bug: event not clickable.)
    if (
      t.closest(
        '.box, .ev, .evlabel, .edge-labels, .endpoint, .cond, irembogov-iwb-event-label, irembogov-iwb-condition-node, .iwb-toolbar, .iwb-drawer-host, irembogov-iwb-add-event, button, input, textarea, select'
      )
    )
      return;
    const el = this.canvasRef?.nativeElement;
    if (!el) return;
    this.panning = true;
    this.panStart = {
      x: e.clientX,
      y: e.clientY,
      sl: el.scrollLeft,
      st: el.scrollTop,
    };
    el.classList.add('grabbing');
    el.setPointerCapture?.(e.pointerId);
  }
  onPanMove(e: PointerEvent): void {
    if (!this.panning) return;
    const el = this.canvasRef?.nativeElement;
    if (!el) return;
    el.scrollLeft = this.panStart.sl - (e.clientX - this.panStart.x);
    el.scrollTop = this.panStart.st - (e.clientY - this.panStart.y);
  }
  endPan(e: PointerEvent): void {
    if (!this.panning) return;
    this.panning = false;
    const el = this.canvasRef?.nativeElement;
    el?.classList.remove('grabbing');
    el?.releasePointerCapture?.(e.pointerId);
  }
  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    this.sizingDirty = true;
  }

  /** Open the fullscreen workflow.json editor seeded with the current workflow. */
  openJsonEditor(): void {
    this.showJsonEditor = true;
  }

  /** Apply JSON edited in the fullscreen editor back into the builder. */
  onJsonEditorSave(parsed: unknown): void {
    this.showJsonEditor = false;
    // Reload the model from the edited JSON; the model$ subscription recomputes
    // `json` and echoes it out via (workflowJsonChange), same as any other edit.
    this.store.load(parsed);
  }

  /**
   * Semantic guard for the fullscreen JSON editor. Returns an error message (which
   * keeps the editor open) or null when `parsed` is a structurally valid workflow.
   *
   * Without this, `JSON.parse` alone accepts `{}` — which the mapper reads as an
   * empty workflow — so a stray edit would silently wipe the visual builder;
   * malformed transitions would likewise reset the store to an empty model.
   * Bound as a field so it can be passed to the child editor via [validate].
   */
  validateWorkflowJson = (parsed: unknown): string | null => {
    if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return 'the root must be a JSON object with a "workflow" array.';
    }
    const hasWorkflow = Object.prototype.hasOwnProperty.call(
      parsed,
      'workflow'
    );
    if (!hasWorkflow) {
      return 'missing a "workflow" array — refusing to save so the workflow is not cleared.';
    }
    const wf = (parsed as { workflow?: unknown }).workflow;
    if (wf === null) {
      return null;
    }
    if (!Array.isArray(wf)) {
      return 'missing a "workflow" array — refusing to save so the workflow is not cleared.';
    }
    for (let i = 0; i < wf.length; i++) {
      const t = wf[i] as Record<string, unknown> | null;
      const where = `transition #${i + 1}`;
      if (t == null || typeof t !== 'object')
        return `${where} is not an object.`;
      if (typeof t['startState'] !== 'string' || !t['startState']) {
        return `${where} is missing a "startState".`;
      }
      if (typeof t['event'] !== 'string' || !t['event']) {
        return `${where} is missing an "event".`;
      }
      const one = t['endStateOne'] as Record<string, unknown> | null;
      if (one == null || typeof one !== 'object' || !one['stateCode']) {
        return `${where} is missing a valid "endStateOne.stateCode".`;
      }
    }
    // Final smoke test: the mapper must be able to parse it without throwing.
    try {
      fromJson(parsed);
    } catch (e) {
      return `the workflow could not be parsed (${(e as Error).message}).`;
    }
    return null;
  };

  // ── template helpers (container resolves model slices for the dumb children) ──
  endStateFor(
    ref: { tid: string; which: 'one' | 'two' } | null
  ): IEndState | null {
    if (!ref) return null;
    const t = this.transition(ref.tid);
    return t ? (ref.which === 'one' ? t.one : t.two) : null;
  }
  transition(tid: string): ITransition {
    return this.model.transitions.find(t => t.id === tid) as ITransition;
  }
  stateName(code: string): string {
    const s = this.model.states.find(x => x.code === code);
    return s ? s.name : code;
  }
  /**
   * Resolve a state's display colour. The model only carries the default grey (the
   * workflow JSON has no colour), so fall back to the backend application-states
   * catalogue (`apiStates`) so a state shows its real colour once loaded from the BE.
   */
  colour(code: string): string {
    const s = this.model.states.find(x => x.code === code);
    if (s && s.colour && s.colour !== DEFAULT_STATE_COLOUR) return s.colour;
    const api = this.apiStates.find(a => a.code === code);
    return api?.colour ?? s?.colour ?? DEFAULT_STATE_COLOUR;
  }

  /** Colour for the NEW node on the empty-state preview — the conventional primary blue,
   *  falling back from the catalog colour so it never renders as the muted default. */
  get emptyStartColour(): string {
    const c = this.colour('NEW');
    return c === DEFAULT_STATE_COLOUR ? '#0063cf' : c;
  }
  isTerminal(code: string): boolean {
    return !this.model.transitions.some(t => t.startState === code);
  }
  trackByIndex(i: number): number {
    return i;
  }
  trackByDecision(_: number, d: { tid: string }): string {
    return d.tid;
  }

  /** Measured size of a node, or a sensible estimate. Decision diamonds (cond:<tid>) are smaller. */
  private sizeOf(code: string): { w: number; h: number } {
    const m = this.nodeSizes[code];
    if (m) return m;
    if (code.startsWith('cond:')) return { w: 240, h: 74 };
    return { w: 258, h: this.estH(code) };
  }
  /** Centre point of a node in render coordinates (null if not yet positioned). */
  private nodeCenter(code: string): { x: number; y: number } | null {
    const p = this.freePos[code];
    if (!p) return null;
    const sz = this.sizeOf(code);
    return { x: p.x + this.off.x + sz.w / 2, y: p.y + this.off.y + sz.h / 2 };
  }

  /** Distance from a box centre to its edge along a unit direction (ux,uy). */
  private edgeDist(hw: number, hh: number, ux: number, uy: number): number {
    const tx = ux !== 0 ? hw / Math.abs(ux) : Infinity;
    const ty = uy !== 0 ? hh / Math.abs(uy) : Infinity;
    return Math.min(tx, ty);
  }

  /**
   * Render-space centre for a guarded transition's ◆ diamond: placed in the MIDDLE OF THE GAP
   * between the source box edge and the outcomes' box edge (clamped to keep clear of both), so
   * it never stacks on the source — works the same in vertical and horizontal layouts.
   */
  private condCenter(t: ITransition): { x: number; y: number } | null {
    if (!t.two) return null;
    const srcC = this.nodeCenter(t.startState);
    const oneC = this.nodeCenter(t.one.stateCode);
    const twoC = this.nodeCenter(t.two.stateCode);
    if (!srcC || !oneC || !twoC) return null;
    const vx = (oneC.x + twoC.x) / 2 - srcC.x;
    const vy = (oneC.y + twoC.y) / 2 - srcC.y;
    const len = Math.hypot(vx, vy) || 1;
    const ux = vx / len;
    const uy = vy / len;
    const srcSz = this.sizeOf(t.startState);
    const oneSz = this.sizeOf(t.one.stateCode);
    const twoSz = this.sizeOf(t.two.stateCode);
    const dsz = this.sizeOf('cond:' + t.id);
    const gapStart = this.edgeDist(srcSz.w / 2, srcSz.h / 2, ux, uy);
    const gapEnd =
      len -
      this.edgeDist((oneSz.w + twoSz.w) / 4, (oneSz.h + twoSz.h) / 4, ux, uy);
    const dHalf = this.edgeDist(dsz.w / 2, dsz.h / 2, ux, uy);
    const MINGAP = 30; // breathing room between the ◆ and either box
    let dist = (gapStart + gapEnd) / 2;
    const minDist = gapStart + MINGAP + dHalf;
    const maxDist = gapEnd - MINGAP - dHalf;
    if (dist < minDist) dist = minDist;
    if (maxDist > minDist && dist > maxDist) dist = maxDist;
    return { x: srcC.x + ux * dist, y: srcC.y + uy * dist };
  }

  /**
   * Decision diamonds to render on the canvas — one per guarded transition (endStateCondition).
   * Uses condCenter() so the ◆ sits exactly where the source→◆→outcome edges meet.
   * Clicking it opens the condition/event editor.
   */
  get decisions(): Array<{
    tid: string;
    x: number;
    y: number;
    t: ITransition;
  }> {
    const out: Array<{ tid: string; x: number; y: number; t: ITransition }> =
      [];
    for (const t of this.model.transitions) {
      if (!t.guard || !t.two) continue;
      const c = this.condCenter(t);
      if (!c) continue;
      out.push({ tid: t.id, x: c.x, y: c.y, t });
    }
    return out;
  }

  // ── inspector open handlers ──
  openState(ref: { tid: string; which: 'one' | 'two' } | null): void {
    if (!ref) return;
    this.inspector = {
      mode: 'state',
      tid: ref.tid,
      which: ref.which,
      stateName: this.stateName(this.endStateFor(ref)?.stateCode || ''),
    };
  }
  /** Open the single-action editor for a clicked action chip. */
  openAction(code: string, action: IAction): void {
    const ref = this.refForCode(code);
    const es = this.endStateForCode(code);
    if (!ref || !es) return;
    const idx = es.actions.indexOf(action);
    if (idx < 0) return;
    this.inspector = {
      mode: 'action',
      tid: ref.tid,
      which: ref.which,
      actionIdx: idx,
      stateName: this.stateName(code),
    };
  }
  /** State view → edit a single action from its list. */
  onInspectorActionEdit(idx: number): void {
    if (this.inspector)
      this.inspector = { ...this.inspector, mode: 'action', actionIdx: idx };
  }
  onInspectorStateEdit(): void {
    if (!this.inspector) return;
    this.inspector = {
      ...this.inspector,
      mode: 'state',
      actionIdx: undefined,
      stateName:
        this.stateName(
          this.endStateFor({
            tid: this.inspector.tid,
            which: this.inspector.which || 'one',
          })?.stateCode || ''
        ) || this.inspector.stateName,
    };
  }
  openEvent(tid: string): void {
    this.inspector = { mode: 'event', tid };
  }
  openCondition(tid: string): void {
    this.inspector = { mode: 'condition', tid };
  }
  closeInspector(): void {
    this.inspector = null;
  }

  // ── highlight the element currently being edited (instead of a black overlay) ──
  get activeStateCode(): string | null {
    if (this.inspector?.mode !== 'state') return null;
    return (
      this.endStateFor({
        tid: this.inspector.tid,
        which: this.inspector.which || 'one',
      })?.stateCode ?? null
    );
  }
  isActiveEdge(e: FreeEdge): boolean {
    return (
      (this.inspector?.mode === 'event' ||
        this.inspector?.mode === 'condition') &&
      this.inspector?.tid === e.tid
    );
  }

  /** Click a warning → centre the offending state on the canvas and flash it. */
  focusWarning(w: Warning): void {
    const code =
      w.stateCode || (w.tid ? this.transition(w.tid)?.startState : undefined);
    if (!code) return;
    this.focusedStateCode = code;
    if (this.focusClearTimer) clearTimeout(this.focusClearTimer);
    this.focusClearTimer = setTimeout(() => {
      this.focusedStateCode = null;
      this.cdr.markForCheck();
    }, 1800);
    // Defer so the flash class is applied before we scroll to the node.
    setTimeout(() => this.scrollNodeIntoView(code), 0);
  }

  /** Centre a state box in the canvas viewport (no page scroll, scale-aware). */
  private scrollNodeIntoView(code: string): void {
    const canvas = this.canvasRef?.nativeElement;
    const node = canvas?.querySelector<HTMLElement>(
      `.free-node[data-code="${code}"]`
    );
    if (!canvas || !node) return;
    const n = node.getBoundingClientRect();
    const c = canvas.getBoundingClientRect();
    const left =
      canvas.scrollLeft +
      (n.left - c.left) -
      (canvas.clientWidth - n.width) / 2;
    const top =
      canvas.scrollTop + (n.top - c.top) - (canvas.clientHeight - n.height) / 2;
    canvas.scrollTo({
      left: Math.max(0, left),
      top: Math.max(0, top),
      behavior: 'smooth',
    });
  }
  /**
   * Open the add-event composer for `code`, anchored near the click point.
   * `anchor` (when triggered from a connection point) records the direction so the
   * resulting new state lands on that side of the source.
   */
  openAddEvent(code: string, ev?: MouseEvent, anchor?: AnchorName): void {
    this.composerFrom = code;
    this.composerOpen = true;
    this.composerFirst = false;
    this.lastComposerFrom = code;
    this.lastComposerDir = anchor ? this.dirOf(anchor) : { dx: 0, dy: 1 };
    const W = 340,
      H = 460,
      m = 12;
    if (ev) {
      this.composerPos = {
        x: Math.min(Math.max(m, ev.clientX + 12), window.innerWidth - W - m),
        y: Math.min(Math.max(m, ev.clientY - 16), window.innerHeight - H - m),
      };
    } else {
      this.composerPos = { x: window.innerWidth - W - 32, y: 96 };
    }
  }

  /**
   * Empty canvas → start the workflow. Every workflow begins at the NEW state with an APPLY
   * event, so the composer opens locked to NEW → APPLY and the user only chooses where it goes.
   */
  startFromScratch(): void {
    this.composerFrom = 'NEW';
    this.composerFirst = true;
    this.lastComposerFrom = 'NEW';
    this.lastComposerDir = { dx: 0, dy: 1 };
    const W = 340;
    this.composerPos = {
      x: Math.max(16, (window.innerWidth - W) / 2),
      y: 120,
    };
    this.composerOpen = true;
  }

  closeComposer(): void {
    this.composerOpen = false;
    this.composerFirst = false;
    this.composerFrom = null;
  }
  private dirOf(a: AnchorName): { dx: number; dy: number } {
    if (a[0] === 't') return { dx: 0, dy: -1 };
    if (a[0] === 'b') return { dx: 0, dy: 1 };
    if (a === 'l') return { dx: -1, dy: 0 };
    return { dx: 1, dy: 0 };
  }

  // ── drag an action chip from one state onto another to duplicate it ──
  draggingAction: IAction | null = null;
  private draggingFromCode: string | null = null;
  onChipDragStart(a: IAction, fromCode: string): void {
    this.draggingAction = a;
    this.draggingFromCode = fromCode;
  }
  onChipDragEnd(): void {
    this.draggingAction = null;
    this.draggingFromCode = null;
  }
  onChipDropTo(ref: { tid: string; which: 'one' | 'two' } | null): void {
    if (!this.draggingAction || !ref) return;
    const targetCode = this.endStateFor(ref)?.stateCode ?? null;
    // dropping back onto the SAME state is a no-op (don't duplicate within a state)
    if (targetCode && targetCode === this.draggingFromCode) return;
    this.store.addActionCopy(ref.tid, ref.which, this.draggingAction);
  }

  // ── copy an event to the clipboard → paste (⌘V) into a new event (see add-event composer) ──
  /** Transient toast (e.g. "event copied", paste hints). */
  copyNotice = '';
  /** Show a hint after an event is copied; the user pastes it via ⌘V in a state's "+ add event". */
  onEventCopied(eventName: string): void {
    this.copyNotice = `Event "${eventName}" copied — open a state's "＋ add event" and press ⌘V, or right-click a state → Paste.`;
    setTimeout(() => {
      this.copyNotice = '';
      this.cdr.markForCheck();
    }, 6000);
    this.cdr.markForCheck();
  }

  // ── right-click context menu (configure / copy an event; paste / add on a state) ──
  ctxMenu: {
    x: number;
    y: number;
    items: Array<{ label: string; run: () => void }>;
  } | null = null;

  /** Right-click an event label → Configure / Copy. */
  onEventContext(tid: string, e: MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this.ctxMenu = {
      x: e.clientX,
      y: e.clientY,
      items: [
        { label: '⚙ Configure event', run: () => this.openEvent(tid) },
        {
          label: '⧉ Copy event',
          run: () => {
            if (this.store.copyEvent(tid))
              this.onEventCopied(this.transition(tid)?.event || '');
          },
        },
        { label: '🗑 Delete event', run: () => this.deleteEvent(tid) },
      ],
    };
    this.cdr.detectChanges(); // render the menu synchronously (robust against zone timing)
  }

  /** Right-click a state node → Paste copied event (if any) / Add event. */
  onStateContext(code: string, e: MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
    const items: Array<{ label: string; run: () => void }> = [
      {
        label: '＋ Add event from here',
        run: () => this.openAddEvent(code, e),
      },
    ];
    const clip = this.store.clipboardEvent;
    if (clip)
      items.unshift({
        label: `📋 Paste event "${clip}"`,
        run: () => this.pasteEventOntoState(code, clip),
      });
    items.push({
      label: '🗑 Delete state',
      run: () => this.deleteStateNode(code),
    });
    this.ctxMenu = { x: e.clientX, y: e.clientY, items };
    this.cdr.detectChanges(); // render the menu synchronously (robust against zone timing)
  }

  private pasteEventOntoState(code: string, ev: string): void {
    const ok = this.store.pasteEventOnto(code);
    this.copyNotice = ok
      ? `Pasted "${ev}" onto ${code}.`
      : `"${code}" already has a "${ev}" event — nothing pasted.`;
    setTimeout(() => {
      this.copyNotice = '';
      this.cdr.markForCheck();
    }, 4000);
    this.cdr.markForCheck();
  }

  // ── delete (event / state) — always behind a confirmation that previews the impact ──
  /** What a pending delete will remove, shown in the confirmation popup. null = closed. */
  confirmDelete: {
    kind: 'event' | 'state' | 'action';
    id: string; // transition id (event) or state code (state)
    which?: 'one' | 'two';
    actionIndex?: number;
    actionType?: string;
    heading: string;
    events: string[]; // event codes that will be removed
    states: string[]; // state codes that will be removed (incl. orphaned downstream states)
  } | null = null;

  /** Compute exactly which events and states disappear once `removeTids` are dropped. */
  private deletionImpact(removeTids: Set<string>): {
    events: string[];
    states: string[];
  } {
    const surviving = new Set<string>();
    for (const t of this.model.transitions) {
      if (removeTids.has(t.id)) continue;
      surviving.add(t.startState);
      if (t.one?.stateCode) surviving.add(t.one.stateCode);
      if (t.two?.stateCode) surviving.add(t.two.stateCode);
    }
    const states = this.model.states
      .map(s => s.code)
      .filter(code => !surviving.has(code));
    const events = [
      ...new Set(
        this.model.transitions
          .filter(t => removeTids.has(t.id))
          .map(t => t.event)
      ),
    ];
    return { events, states };
  }

  /** Ask before removing an event (transition) and any end state it leads to. */
  deleteEvent(tid: string): void {
    const t = this.transition(tid);
    if (!t) return;
    const impact = this.deletionImpact(new Set([tid]));
    this.confirmDelete = {
      kind: 'event',
      id: tid,
      heading: `Delete event “${t.event || 'event'}”?`,
      events: impact.events,
      states: impact.states,
    };
    this.cdr.detectChanges();
  }

  /** Ask before removing a state and every event into or out of it. */
  deleteStateNode(code: string): void {
    const removeTids = new Set(
      this.model.transitions
        .filter(
          t =>
            t.startState === code ||
            t.one?.stateCode === code ||
            t.two?.stateCode === code
        )
        .map(t => t.id)
    );
    if (!removeTids.size) return;
    const impact = this.deletionImpact(removeTids);
    this.confirmDelete = {
      kind: 'state',
      id: code,
      heading: `Delete state “${this.stateName(code) || code}”?`,
      events: impact.events,
      states: impact.states,
    };
    this.cdr.detectChanges();
  }

  /** Ask before removing an action from a state. */
  deleteAction(req: InspectorActionDeleteRequest): void {
    this.confirmDelete = {
      kind: 'action',
      id: req.tid,
      which: req.which,
      actionIndex: req.index,
      actionType: req.actionType,
      heading: `Delete action “${req.actionType || 'action'}”?`,
      events: [],
      states: [],
    };
    this.cdr.detectChanges();
  }

  /** User confirmed the popup → perform the deletion. */
  confirmDeleteRun(): void {
    const c = this.confirmDelete;
    if (!c) return;
    this.confirmDelete = null;
    if (c.kind === 'action') {
      if (c.which && c.actionIndex != null) {
        this.store.removeAction(c.id, c.which, c.actionIndex);
        if (
          this.inspector?.mode === 'action' &&
          this.inspector.tid === c.id &&
          this.inspector.which === c.which &&
          this.inspector.actionIdx === c.actionIndex
        ) {
          this.onInspectorStateEdit();
        }
        this.notice(`Deleted action “${c.actionType || 'action'}”.`);
      }
      return;
    }
    if (this.inspector?.tid === c.id || c.kind === 'state')
      this.closeInspector();
    if (c.kind === 'event') {
      this.store.deleteTransition(c.id);
      this.notice(`Deleted event “${c.events[0] || ''}”.`);
    } else {
      this.store.deleteState(c.id);
      this.notice(`Deleted state and ${c.events.length} connected event(s).`);
    }
  }

  confirmDeleteCancel(): void {
    this.confirmDelete = null;
  }

  /** Transient toast helper shared by the destructive (delete) actions. */
  private notice(msg: string): void {
    this.copyNotice = msg;
    setTimeout(() => {
      this.copyNotice = '';
      this.cdr.markForCheck();
    }, 4000);
    this.cdr.markForCheck();
  }

  runCtx(item: { run: () => void }): void {
    item.run();
    this.ctxMenu = null;
  }
  closeCtxMenu(): void {
    this.ctxMenu = null;
  }
  @HostListener('document:keydown.escape') onEscape(): void {
    if (this.confirmDelete) this.confirmDeleteCancel();
    else if (this.ctxMenu) this.closeCtxMenu();
  }

  // ── free mode: per-state representative endState / ref ──
  endStateForCode(code: string): IEndState | null {
    for (const t of this.model.transitions) {
      if (t.one.stateCode === code) return t.one;
      if (t.two && t.two.stateCode === code) return t.two;
    }
    return null;
  }
  refForCode(code: string): { tid: string; which: 'one' | 'two' } | null {
    for (const t of this.model.transitions) {
      if (t.one.stateCode === code) return { tid: t.id, which: 'one' };
      if (t.two && t.two.stateCode === code) return { tid: t.id, which: 'two' };
    }
    return null;
  }
  toggleFreeMode(): void {
    this.freeMode ? this.exitFreeMode() : this.enterFreeMode();
  }
  exitFreeMode(): void {
    this.freeMode = false;
    this.sizingDirty = true;
  }
  enterFreeMode(): void {
    this.freeMode = true;
    this.seeded = false;
    this.sizingDirty = true;
  }

  /** Positions already saved on the flow's endState nodes (raw.position), keyed by state code. */
  private collectSavedPositions(): Record<string, { x: number; y: number }> {
    const pos: Record<string, { x: number; y: number }> = {};
    for (const t of this.model.transitions) {
      for (const es of [t.one, t.two]) {
        if (!es) continue;
        const p = (
          es.raw as { position?: { x: number; y: number } } | undefined
        )?.position;
        if (
          p &&
          typeof p.x === 'number' &&
          typeof p.y === 'number' &&
          !pos[es.stateCode]
        ) {
          pos[es.stateCode] = { x: p.x, y: p.y };
        }
      }
    }
    return pos;
  }

  /**
   * First render in free mode. KEEP positions the flow already carries (user-set / BE-stored)
   * so switching JSON↔Design — or reopening a saved flow — never reformats the user's layout.
   * Only a fresh flow with NO saved positions is auto-tidied; unpositioned states (e.g. a start
   * state, which never carries a position) are placed relative to their neighbours.
   */
  private seedFreePositions(): void {
    this.freeEdgeAnchors = {}; // fresh workflow → drop stale anchor choices
    const saved = this.collectSavedPositions();
    if (Object.keys(saved).length > 0) {
      this.freePos = { ...saved };
      this.placeMissingPositions(); // start/new states without a saved position
      this.pendingTidy = false; // honour saved positions — do NOT re-tidy
    } else {
      this.freePos = this.tidyLayout();
      this.pendingTidy = true; // refine with real measured sizes once nodes render
    }
    this.recomputeFreeSize();
    this.scheduleFreeRecompute();
  }

  /** "Tidy up" — re-arrange every box into a clean, well-spaced flow. Stays draggable. */
  tidyUp(dir?: 'vertical' | 'horizontal'): void {
    if (dir) this.tidyDir = dir;
    this.freePos = this.tidyLayout();
    // re-resolve connection points to match the new flow direction
    // (vertical → bottom→top, horizontal → right→left, chosen by geometry).
    this.freeEdgeAnchors = {};
    this.recomputeFreeSize();
    this.store.setPositions(this.freePos); // persist so the tidy layout sticks + round-trips
    this.scheduleFreeRecompute();
  }

  /** Position of the first outgoing target of `code` that already has one (else null). */
  private firstPositionedTarget(code: string): { x: number; y: number } | null {
    for (const t of this.model.transitions) {
      if (t.startState !== code) continue;
      if (this.freePos[t.one.stateCode]) return this.freePos[t.one.stateCode];
      if (t.two && this.freePos[t.two.stateCode]) {
        return this.freePos[t.two.stateCode];
      }
    }
    return null;
  }

  /**
   * Place new states (no position yet) next to the state they were added from,
   * in the direction of the connection point used (falls back to "below"). Root states
   * (no incoming) are placed just before their first positioned target.
   */
  private placeMissingPositions(): void {
    const W = 258;
    let changed = false;
    for (const s of this.model.states) {
      if (this.freePos[s.code]) continue;
      let srcCode: string | null = null;
      for (const t of this.model.transitions) {
        const dests = t.two
          ? [t.one.stateCode, t.two.stateCode]
          : [t.one.stateCode];
        if (t.startState !== s.code && dests.includes(s.code)) {
          srcCode = t.startState;
          break;
        }
      }
      const src = srcCode ? this.freePos[srcCode] : null;
      let pos = { x: 40, y: 40 };
      if (src && srcCode) {
        const dir =
          srcCode === this.lastComposerFrom
            ? this.lastComposerDir
            : { dx: 0, dy: 1 };
        const srcH = this.estH(srcCode);
        let dy = 0;
        if (dir.dy > 0) dy = srcH + 70;
        else if (dir.dy < 0) dy = -(this.estH(s.code) + 70);
        pos = {
          x: Math.max(0, src.x + dir.dx * (W + 80)),
          y: Math.max(0, src.y + dy),
        };
      } else {
        // root state (no incoming): place it just before its first positioned outgoing
        // target so it doesn't pile up at the origin with other roots.
        const tgtPos = this.firstPositionedTarget(s.code);
        if (tgtPos) {
          pos =
            this.tidyDir === 'horizontal'
              ? { x: tgtPos.x - (W + 130), y: tgtPos.y }
              : { x: tgtPos.x, y: tgtPos.y - (this.estH(s.code) + 130) };
        }
      }
      this.freePos = { ...this.freePos, [s.code]: pos };
      this.justAddedCode = s.code; // newest unplaced state → scroll it into view after render
      changed = true;
    }
    // NOTE: placement is local only — persisting here (inside the model subscription)
    // would re-enter the store and double-emit. It sticks once the user drags/tidies.
    if (changed) this.recomputeFreeSize();
  }

  /** Layered top-down layout (BFS levels from start states) with comfortable gaps. */
  private tidyLayout(): Record<string, { x: number; y: number }> {
    const codes = this.model.states.map(s => s.code);
    const adj: Record<string, string[]> = {};
    const indeg: Record<string, number> = {};
    codes.forEach(c => {
      adj[c] = [];
      indeg[c] = 0;
    });
    for (const t of this.model.transitions) {
      const dests = t.two
        ? [t.one.stateCode, t.two.stateCode]
        : [t.one.stateCode];
      for (const d of dests) {
        if (
          d !== t.startState &&
          adj[t.startState] &&
          !adj[t.startState].includes(d)
        ) {
          adj[t.startState].push(d);
          indeg[d]++;
        }
      }
    }
    let roots = codes.filter(c => indeg[c] === 0);
    if (!roots.length && codes.length) roots = [codes[0]];
    const level: Record<string, number> = {};
    const q = [...roots];
    roots.forEach(r => (level[r] = 0));
    while (q.length) {
      const c = q.shift();
      if (c === undefined) break;
      for (const d of adj[c])
        if (level[d] === undefined) {
          level[d] = level[c] + 1;
          q.push(d);
        }
    }
    codes.forEach(c => {
      if (level[c] === undefined) level[c] = 0;
    });
    const byLevel: Record<number, string[]> = {};
    codes.forEach(c => {
      (byLevel[level[c]] ??= []).push(c);
    });
    const order = Object.keys(byLevel)
      .map(Number)
      .sort((a, b) => a - b);
    const W = 258,
      hGap = 130,
      vGap = 120,
      start = 40;
    // Levels that feed a ◆ decision need extra room so the diamond + branch labels sit clearly
    // between source and outcomes. Horizontal needs much more (the card lays out side-by-side).
    const DECISION_GAP = this.tidyDir === 'horizontal' ? 480 : 150;
    const decAfter: Record<number, boolean> = {};
    for (const t of this.model.transitions) {
      if (t.guard && level[t.startState] !== undefined) {
        decAfter[level[t.startState]] = true;
      }
    }
    const pos: Record<string, { x: number; y: number }> = {};

    if (this.tidyDir === 'horizontal') {
      // levels flow LEFT→RIGHT; siblings stack vertically (better for wide graphs)
      const tallest = Math.max(
        ...order.map(L =>
          byLevel[L].reduce((s, c) => s + this.estH(c) + vGap, -vGap)
        ),
        120
      );
      let x = start;
      for (const L of order) {
        const col = byLevel[L];
        const colH = col.reduce((s, c) => s + this.estH(c) + vGap, -vGap);
        let y = start + Math.max(0, (tallest - colH) / 2);
        for (const c of col) {
          pos[c] = { x: Math.round(x), y: Math.round(y) };
          y += this.estH(c) + vGap;
        }
        x += W + hGap + (decAfter[L] ? DECISION_GAP : 0);
      }
      return pos;
    }

    // vertical (default): levels flow TOP→DOWN; siblings spread horizontally
    const widest = Math.max(
      ...order.map(L => byLevel[L].length * W + (byLevel[L].length - 1) * hGap),
      W
    );
    let y = start;
    for (const L of order) {
      const row = byLevel[L];
      const rowW = row.length * W + (row.length - 1) * hGap;
      let x = start + Math.max(0, (widest - rowW) / 2);
      let maxH = 0;
      for (const c of row) {
        pos[c] = { x: Math.round(x), y: Math.round(y) };
        x += W + hGap;
        maxH = Math.max(maxH, this.estH(c));
      }
      y += maxH + vGap + (decAfter[L] ? DECISION_GAP : 0);
    }
    return pos;
  }
  private estH(code: string): number {
    const s = this.nodeSizes[code];
    if (s) return s.h;
    const es = this.endStateForCode(code);
    return 96 + (es ? es.actions.length * 24 : 0) + (es?.nextEvent ? 20 : 0);
  }

  /** Defer node measuring + edge routing to the next tick (after the free-layer renders). */
  private scheduleFreeRecompute(): void {
    setTimeout(() => {
      if (!this.freeMode) return;
      this.measureNodes();
      if (this.pendingTidy) {
        this.pendingTidy = false;
        this.freePos = this.tidyLayout();
        this.recomputeFreeSize();
      }
      this.computeFreeEdges();
      if (this.justAddedCode) {
        this.scrollCodeIntoView(this.justAddedCode);
        this.justAddedCode = null;
      }
      this.cdr.markForCheck();
      // A just-added node may not have painted yet, so its edges used an estimated size.
      // Re-measure once on the next tick so the geometry snaps to the real node size.
      const unmeasured =
        this.model.states.some(
          s => this.freePos[s.code] && !this.nodeSizes[s.code]
        ) || this.decisions.some(d => !this.nodeSizes['cond:' + d.tid]);
      if (unmeasured && !this.remeasureScheduled) {
        this.remeasureScheduled = true;
        setTimeout(() => {
          this.remeasureScheduled = false;
          if (!this.freeMode) return;
          this.measureNodes();
          this.computeFreeEdges();
          this.recomputeFreeSize();
          this.cdr.markForCheck();
        }, 80);
      }
    });
  }

  /**
   * Scroll the free canvas so a (newly added) node is clearly in view. The canvas can be
   * taller than the visible window, so place the node near the TOP of the canvas viewport
   * (rather than its centre) to guarantee it sits above the window fold.
   */
  private scrollCodeIntoView(code: string): void {
    const el = this.canvasRef?.nativeElement;
    if (!el || !this.freePos[code]) return;
    const sz = this.nodeSizes[code] ?? { w: 258, h: this.estH(code) };
    const x = this.renderX(code) * this.scale,
      y = this.renderY(code) * this.scale;
    el.scrollLeft = Math.max(
      0,
      x + (sz.w * this.scale) / 2 - el.clientWidth / 2
    );
    el.scrollTop = Math.max(0, y - 96);
  }
  private measureNodes(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    const sizes: Record<string, { w: number; h: number }> = {};
    // measure both state boxes and ◆ decision cards so edges anchor to their real edges
    canvas
      .querySelectorAll('.free-node, .iwb-decision')
      .forEach((n: Element) => {
        const code = n.getAttribute('data-code');
        const el = n as HTMLElement;
        if (code) sizes[code] = { w: el.offsetWidth, h: el.offsetHeight };
      });
    this.nodeSizes = sizes;
  }

  /** Pointer-drag a free-mode box to reposition it (click without movement still opens config). */
  onNodePointerDown(code: string, e: PointerEvent): void {
    if (e.button !== 0) return; // only left-button drags (right-click → context menu)
    if (
      (e.target as HTMLElement).closest(
        '.achip, .iwb-anchor, button, .addbtn, .mini, input, textarea, select'
      )
    )
      return;
    const startX = e.clientX,
      startY = e.clientY;
    const orig = this.freePos[code] ?? { x: 0, y: 0 };
    const scale = this.scale;
    let moved = false;
    const move = (ev: PointerEvent) => {
      if (
        !moved &&
        (Math.abs(ev.clientX - startX) > 4 || Math.abs(ev.clientY - startY) > 4)
      )
        moved = true;
      if (!moved) return;
      this.freePos = {
        ...this.freePos,
        [code]: {
          x: Math.round(orig.x + (ev.clientX - startX) / scale),
          y: Math.round(orig.y + (ev.clientY - startY) / scale),
        },
      };
      // No clamp — if the box moves past the left/top edge, the offset grows and we
      // scroll-compensate so the box stays under the pointer (infinite canvas, no wall).
      const prev = { x: this.off.x, y: this.off.y };
      this.updateOffset();
      const el = this.canvasRef?.nativeElement;
      if (el) {
        el.scrollLeft += (this.off.x - prev.x) * scale;
        el.scrollTop += (this.off.y - prev.y) * scale;
      }
      this.recomputeFreeSize();
      this.computeFreeEdges(); // pure (uses cached node sizes) → edges follow the drag live
      this.cdr.markForCheck();
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      if (!moved) return;
      const pos = this.freePos[code];
      this.store.setPosition(code, pos); // round-trips to endState raw.position
      this.recomputeFreeSize();
      // swallow the click that follows a drag so it doesn't open the inspector
      const supp = (ce: Event) => {
        ce.stopPropagation();
        ce.preventDefault();
        document.removeEventListener('click', supp, true);
      };
      document.addEventListener('click', supp, true);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  }
  private recomputeFreeSize(): void {
    this.updateOffset();
    let w = 700,
      h = 460;
    for (const c of Object.keys(this.freePos)) {
      const sz = this.nodeSizes[c];
      w = Math.max(w, this.renderX(c) + (sz ? sz.w : 258) + this.MARGIN);
      h = Math.max(
        h,
        this.renderY(c) + (sz ? sz.h : this.estH(c)) + this.MARGIN
      );
    }
    this.freeSize = { w: Math.ceil(w), h: Math.ceil(h) };
    this.sizingDirty = true;
    this.cdr.markForCheck();
  }
  // ── render coordinates (logical position + offset) ──
  renderX(code: string): number {
    const p = this.freePos[code];
    return p ? p.x + this.off.x : 0;
  }
  renderY(code: string): number {
    const p = this.freePos[code];
    return p ? p.y + this.off.y : 0;
  }
  private updateOffset(): void {
    const xs = Object.values(this.freePos).map(p => p.x);
    const ys = Object.values(this.freePos).map(p => p.y);
    this.off = {
      x: this.MARGIN - (xs.length ? Math.min(...xs) : 0),
      y: this.MARGIN - (ys.length ? Math.min(...ys) : 0),
    };
  }

  // ── anchor geometry ──
  /** Render-space point of a named anchor on a state box. */
  /** Render-space centre + size of any node (state via freePos, or a ◆ diamond via condBox). */
  private boxCenterSize(
    code: string
  ): { cx: number; cy: number; w: number; h: number } | null {
    const cb = this.condBox[code];
    if (cb) return cb;
    const p = this.freePos[code];
    if (!p) return null;
    const s = this.sizeOf(code);
    return {
      cx: p.x + this.off.x + s.w / 2,
      cy: p.y + this.off.y + s.h / 2,
      w: s.w,
      h: s.h,
    };
  }
  anchorPoint(code: string, name: AnchorName): { x: number; y: number } | null {
    const b = this.boxCenterSize(code);
    if (!b) return null;
    const a = this.anchorFr[name];
    return {
      x: b.cx - b.w / 2 + a.fx * b.w,
      y: b.cy - b.h / 2 + a.fy * b.h,
    };
  }
  /** Default source/target anchors chosen by the boxes' relative position (states or ◆ diamond). */
  private chooseAnchors(
    srcCode: string,
    dstCode: string
  ): { src: AnchorName; dst: AnchorName } {
    const s = this.boxCenterSize(srcCode);
    const d = this.boxCenterSize(dstCode);
    if (!s || !d) return { src: 'b2', dst: 't2' };
    const dx = d.cx - s.cx,
      dy = d.cy - s.cy;
    if (Math.abs(dy) >= Math.abs(dx))
      return dy >= 0 ? { src: 'b2', dst: 't2' } : { src: 't2', dst: 'b2' };
    return dx >= 0 ? { src: 'r', dst: 'l' } : { src: 'l', dst: 'r' };
  }
  /** When both branches of a ◆ leave via the same anchor, spread them along that side. */
  private spreadBranchAnchors(
    a: AnchorName,
    b: AnchorName
  ): { one: AnchorName; two: AnchorName } {
    if (a !== b) return { one: a, two: b };
    const side: Record<string, [AnchorName, AnchorName]> = {
      b1: ['b1', 'b3'],
      b2: ['b1', 'b3'],
      b3: ['b1', 'b3'],
      t1: ['t1', 't3'],
      t2: ['t1', 't3'],
      t3: ['t1', 't3'],
      l: ['t1', 'b1'],
      r: ['t3', 'b3'],
    };
    const [one, two] = side[a] ?? ['b1', 'b3'];
    return { one, two };
  }
  /** Cubic control point that leaves an anchor along its outward normal. */
  private ctrl(
    name: AnchorName,
    x: number,
    y: number,
    off: number
  ): { x: number; y: number } {
    if (name[0] === 't') return { x, y: y - off };
    if (name[0] === 'b') return { x, y: y + off };
    if (name === 'l') return { x: x - off, y };
    return { x: x + off, y };
  }

  /**
   * Recompute the free-mode connector edges (one per transition end-state) as SVG
   * cubic-bezier paths between chosen connection-point anchors. Self-guards via a
   * key so it never loops through change detection.
   */
  private computeFreeEdges(): void {
    const edges: FreeEdge[] = [];
    const condBox: typeof this.condBox = {};

    // Generic edge builder between any two nodes (states OR a ◆ diamond, keyed cond:<tid>).
    // Both ends use chosen connection-point anchors, re-routable via freeEdgeAnchors.
    const pushEdge = (
      id: string,
      srcCode: string,
      dstCode: string,
      opts: {
        tid: string;
        label: string;
        actor: string;
        colour: string;
        isSystem: boolean;
        unauthorized: boolean;
        outcome: '' | '①' | '②';
        branch: '' | 'true' | 'false';
        defaultAnchors?: { src: AnchorName; dst: AnchorName };
      }
    ): void => {
      if (!this.boxCenterSize(srcCode) || !this.boxCenterSize(dstCode)) return;
      // self-loop (a state transitioning to itself): draw a loop on the right side so the
      // event stays VISIBLE and clickable — otherwise it's invisible yet still blocks adding
      // another event of the same name.
      if (srcCode === dstCode) {
        const a = this.anchorPoint(srcCode, 't3');
        const b = this.anchorPoint(srcCode, 'b3');
        if (!a || !b) return;
        const LOOP = 96;
        edges.push({
          id,
          tid: opts.tid,
          srcCode,
          dstCode,
          srcName: 't3',
          dstName: 'b3',
          sx: a.x,
          sy: a.y,
          ex: b.x,
          ey: b.y,
          d: `M ${a.x} ${a.y} C ${a.x + LOOP} ${a.y} ${b.x + LOOP} ${b.y} ${
            b.x
          } ${b.y}`,
          mx: Math.max(a.x, b.x) + LOOP * 0.66,
          my: (a.y + b.y) / 2,
          label: opts.label,
          actor: opts.actor,
          colour: opts.colour,
          isSystem: opts.isSystem,
          unauthorized: opts.unauthorized,
          outcome: opts.outcome,
          branch: opts.branch,
        });
        return;
      }
      const existing = this.freeEdgeAnchors[id];
      let srcName = existing?.src;
      let dstName = existing?.dst;
      if (srcName === undefined || dstName === undefined) {
        const def = opts.defaultAnchors ?? this.chooseAnchors(srcCode, dstCode);
        srcName = srcName ?? def.src;
        dstName = dstName ?? def.dst;
        this.freeEdgeAnchors[id] = { src: srcName, dst: dstName };
      }
      const s = this.anchorPoint(srcCode, srcName);
      const d = this.anchorPoint(dstCode, dstName);
      if (!s || !d) return; // node not laid out yet — skip rather than emit NaN geometry
      const off = Math.max(36, Math.hypot(d.x - s.x, d.y - s.y) / 2.4);
      const c1 = this.ctrl(srcName, s.x, s.y, off);
      const c2 = this.ctrl(dstName, d.x, d.y, off);
      edges.push({
        id,
        tid: opts.tid,
        srcCode,
        dstCode,
        srcName,
        dstName,
        sx: s.x,
        sy: s.y,
        ex: d.x,
        ey: d.y,
        d: `M ${s.x} ${s.y} C ${c1.x} ${c1.y} ${c2.x} ${c2.y} ${d.x} ${d.y}`,
        mx: (s.x + d.x) / 2,
        my: (s.y + d.y) / 2,
        label: opts.label,
        actor: opts.actor,
        colour: opts.colour,
        isSystem: opts.isSystem,
        unauthorized: opts.unauthorized,
        outcome: opts.outcome,
        branch: opts.branch,
      });
    };

    for (const t of this.model.transitions) {
      const srcC = this.nodeCenter(t.startState);
      const oneC = this.nodeCenter(t.one.stateCode);
      const twoC = t.two ? this.nodeCenter(t.two.stateCode) : null;
      const isSystem = !!t.authorisation?.systemAuthorised;
      const unauthorized = !isAuthorised(t);

      // ── guarded transition → route source → ◆ decision → (False ①, True ②) ──
      if (t.guard && t.two && srcC && oneC && twoC) {
        const key = 'cond:' + t.id;
        const dsz = this.sizeOf(key);
        const cc = this.condCenter(t); // non-null: srcC/oneC/twoC guarded above
        if (!cc) continue;
        condBox[key] = { cx: cc.x, cy: cc.y, w: dsz.w, h: dsz.h };
        this.condBox = condBox; // expose to anchorPoint/chooseAnchors for this build

        pushEdge(t.id + '→cond', t.startState, key, {
          tid: t.id,
          label: t.event,
          actor: this.actorFor(t),
          colour: '#475467',
          isSystem,
          unauthorized,
          outcome: '',
          branch: '',
        });
        // spread the two branch exits so they don't overlap on the diamond
        const sp = this.spreadBranchAnchors(
          this.chooseAnchors(key, t.one.stateCode).src,
          this.chooseAnchors(key, t.two.stateCode).src
        );
        pushEdge(t.id + '→one→' + t.one.stateCode, key, t.one.stateCode, {
          tid: t.id,
          label: '',
          actor: '',
          colour: this.colour(t.one.stateCode),
          isSystem: false,
          unauthorized: false,
          outcome: '①',
          branch: 'false',
          defaultAnchors: {
            src: sp.one,
            dst: this.chooseAnchors(key, t.one.stateCode).dst,
          },
        });
        pushEdge(t.id + '→two→' + t.two.stateCode, key, t.two.stateCode, {
          tid: t.id,
          label: '',
          actor: '',
          colour: this.colour(t.two.stateCode),
          isSystem: false,
          unauthorized: false,
          outcome: '②',
          branch: 'true',
          defaultAnchors: {
            src: sp.two,
            dst: this.chooseAnchors(key, t.two.stateCode).dst,
          },
        });
        continue;
      }

      // ── normal transition (or guarded with missing positions) → direct edge(s) ──
      const dests: Array<{ es: IEndState; which: 'one' | 'two' }> = t.two
        ? [
            { es: t.one, which: 'one' },
            { es: t.two, which: 'two' },
          ]
        : [{ es: t.one, which: 'one' }];
      for (const { es, which } of dests) {
        if (this.isStuckSystemSelfLoop(t, es)) continue;
        pushEdge(
          t.id + '→' + which + '→' + es.stateCode,
          t.startState,
          es.stateCode,
          {
            tid: t.id,
            label: t.event,
            actor: this.actorFor(t),
            colour: this.colour(es.stateCode),
            isSystem,
            unauthorized,
            outcome: t.two ? (which === 'one' ? '①' : '②') : '',
            branch: '',
          }
        );
      }
    }

    this.condBox = condBox;
    const key = JSON.stringify(edges);
    if (key !== this._edgesKey) {
      this._edgesKey = key;
      this.freeEdges = edges;
      this.cdr.markForCheck();
    }
  }

  private isStuckSystemSelfLoop(t: ITransition, es: IEndState): boolean {
    if (WORKFLOW_VALIDATION_CONFIG.callbackEvents.includes(t.event)) {
      return false;
    }
    return (
      t.authorisation?.systemAuthorised === true &&
      t.startState === es.stateCode &&
      [t.one, t.two]
        .filter(Boolean)
        .every(outcome => outcome?.stateCode === t.startState)
    );
  }

  private actorFor(t: ITransition): string {
    const a = t.authorisation;
    if (a?.systemAuthorised) return 'System';
    if (a?.authorisedRoles?.length) return a.authorisedRoles.join(', ');
    return '—';
  }

  // ── connection points: drag from an anchor → add an event from that state ──
  onAnchorAddEvent(code: string, anchor: AnchorName, e: MouseEvent): void {
    e.stopPropagation();
    e.preventDefault();
    this.openAddEvent(code, e, anchor);
  }

  // ── re-route: drag an edge endpoint to a different anchor on its own box ──
  onEndpointDown(edge: FreeEdge, end: 'src' | 'dst', e: PointerEvent): void {
    e.stopPropagation();
    e.preventDefault();
    const code = end === 'src' ? edge.srcCode : edge.dstCode;
    const move = (ev: PointerEvent) => {
      const pt = this.toLayerPoint(ev.clientX, ev.clientY);
      if (!pt) return;
      const nearest = this.nearestAnchor(code, pt);
      if (!nearest) return;
      const cur = this.freeEdgeAnchors[edge.id] || {};
      if (cur[end] === nearest) return;
      this.freeEdgeAnchors = {
        ...this.freeEdgeAnchors,
        [edge.id]: { ...cur, [end]: nearest },
      };
      this.computeFreeEdges();
      this.cdr.markForCheck();
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  }
  private toLayerPoint(
    clientX: number,
    clientY: number
  ): { x: number; y: number } | null {
    const flow = this.flowRef?.nativeElement;
    if (!flow) return null;
    const r = flow.getBoundingClientRect();
    return {
      x: (clientX - r.left) / this.scale,
      y: (clientY - r.top) / this.scale,
    };
  }
  private nearestAnchor(
    code: string,
    pt: { x: number; y: number }
  ): AnchorName | null {
    let best: AnchorName | null = null,
      bestD = Infinity;
    for (const name of this.anchorNames) {
      const a = this.anchorPoint(code, name);
      if (!a) continue;
      const dd = (a.x - pt.x) ** 2 + (a.y - pt.y) ** 2;
      if (dd < bestD) {
        bestD = dd;
        best = name;
      }
    }
    return best;
  }
  anchorStyle(code: string, name: AnchorName): { [k: string]: string } {
    const a = this.anchorFr[name];
    return { left: a.fx * 100 + '%', top: a.fy * 100 + '%' };
  }

  trackByCode(_i: number, s: { code: string }): string {
    return s.code;
  }
  trackByEdge(_i: number, e: { id: string }): string {
    return e.id;
  }

  // ── reused wizard/v1 form managers ──
  openManager(m: 'notification' | 'office' | 'receipt'): void {
    this.manager = m;
  }
  closeManager(): void {
    this.manager = null;
  }

  /**
   * Merge an update emitted by a reused manager back into the store.
   * The notification / office / receipt forms emit the workflow ARRAY; other
   * deltas (full objects) are spread. Mirrors the wizard's onWorkflowUpdate so
   * the reused forms behave identically inside v2.
   */
  onManagerUpdate(updated: unknown): void {
    const base = (this.json as Record<string, unknown>) ?? {};
    const merged = Array.isArray(updated)
      ? { ...base, workflow: updated }
      : { ...base, ...(updated as Record<string, unknown>) };
    this.store.load(merged);
  }
}
